// ═══════════════════════════════════════════════════════════════
// DEBRIS — Pre-Submission Audit
// Global gap analysis for active disaster before FEMA submission
// Checks: scans, truck certs, CYD, disposal sites, zones, trucks, recon
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, Alert, Modal, SimpleGrid, Divider,
} from '../../components/gab/GabComponents';
import {
  IconAlertTriangle, IconShieldCheck, IconRefresh, IconCheck,
  IconChevronDown, IconChevronUp, IconAlertCircle, IconExternalLink,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { queryRecords, fetchRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useDebrisStore } from '../../store/debris-store';
import { useAllTickets } from '../../hooks/useAllTickets';
import { DEBRIS_TYPE_LABELS, TICKET_STATUS_LABELS } from '../../types/debris';
import type { DebrisType, TicketStatus } from '../../types/debris';

const FD = FIELDS.debrisDocuments;
const FT = FIELDS.debrisLoadTickets;
const FTruck = FIELDS.debrisTrucks;

type Severity = 'critical' | 'important';

interface CheckResult {
  id: string;
  label: string;
  description: string;
  severity: Severity;
  passed: boolean;
  count: number;
  columns: string[];
  items: string[][];
  itemIds?: (string | null)[];   // parallel array — record ID to navigate to, or null
  truncated?: boolean;
}

interface AuditResults {
  ranAt: Date;
  checks: CheckResult[];
  totalTickets: number;
}

// ── Sort state helper ──────────────────────────────────────────
function useSortState() {
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggle = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sort = (rows: string[][], colIndex: number) =>
    [...rows].sort((a, b) => {
      const va = a[colIndex] ?? '';
      const vb = b[colIndex] ?? '';
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  return { sortField, sortDir, toggle, sort };
}

// ── Single check card ──────────────────────────────────────────
function CheckCard({ check, expanded, onToggle, onRowClick }: {
  check: CheckResult;
  expanded: boolean;
  onToggle: () => void;
  onRowClick?: (id: string) => void;
}) {
  const { sortField, sortDir, toggle: toggleSort, sort } = useSortState();

  const severityColor = check.severity === 'critical' ? '#D33423' : '#885604';
  const borderColor = check.passed ? '#037730' : severityColor;

  const displayItems = sortField
    ? sort(check.items, check.columns.indexOf(sortField))
    : check.items;

  return (
    <Paper style={{
      borderLeft: `4px solid ${borderColor}`,
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {/* Card header — always visible */}
      <div
        onClick={!check.passed ? onToggle : undefined}
        style={{
          padding: '14px 16px',
          cursor: !check.passed ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
        }}
      >
        <Group gap={10} style={{ flex: 1 }}>
          {check.passed
            ? <IconCheck size={18} color="#037730" />
            : check.severity === 'critical'
              ? <IconAlertTriangle size={18} color="#D33423" />
              : <IconAlertCircle size={18} color="#885604" />
          }
          <div style={{ flex: 1 }}>
            <Text fw={600} size="sm" style={{ color: '#1e293b' }}>{check.label}</Text>
            <Text size="xs" c="dimmed" style={{ marginTop: 2 }}>{check.description}</Text>
          </div>
        </Group>
        <Group gap={8} style={{ flexShrink: 0 }}>
          <Badge
            color={check.severity === 'critical' ? 'red' : 'yellow'}
            variant="light"
            size="xs"
          >
            {check.severity.toUpperCase()}
          </Badge>
          {check.passed ? (
            <Badge color="green" variant="light" size="sm">PASS</Badge>
          ) : (
            <Badge color={check.severity === 'critical' ? 'red' : 'orange'} variant="light" size="sm">
              {check.count.toLocaleString()} ISSUE{check.count !== 1 ? 'S' : ''}
            </Badge>
          )}
          {!check.passed && (
            <span style={{ color: '#94a3b8' }}>
              {expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            </span>
          )}
        </Group>
      </div>

      {/* Expandable table */}
      {!check.passed && expanded && (
        <div style={{ borderTop: '1px solid #e8eaf0', padding: '0 16px 16px' }}>
          {check.truncated && (
            <Alert color="yellow" style={{ margin: '12px 0 8px' }}>
              Showing first 500 records. {check.count.toLocaleString()} total issues found.
            </Alert>
          )}
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 400px)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                  {check.columns.map((col) => (
                    <th
                      key={col}
                      onClick={() => toggleSort(col)}
                      style={{
                        textAlign: 'left',
                        padding: '8px 12px',
                        borderBottom: '1px solid #e8eaf0',
                        cursor: 'pointer',
                        userSelect: 'none',
                        color: sortField === col ? '#4B3FFF' : '#64748b',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {col}{' '}
                      {sortField === col
                        ? (sortDir === 'desc' ? '↓' : '↑')
                        : <span style={{ opacity: 0.25 }}>↑</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayItems.map((row, i) => {
                  const rowId = check.itemIds?.[i] ?? null;
                  const clickable = !!rowId && !!onRowClick;
                  return (
                    <tr key={i}
                      onClick={clickable ? () => onRowClick!(rowId!) : undefined}
                      style={{
                        background: i % 2 === 0 ? '#fff' : '#f8fafc',
                        cursor: clickable ? 'pointer' : 'default',
                      }}
                      onMouseEnter={clickable ? e => (e.currentTarget.style.background = '#eef2ff') : undefined}
                      onMouseLeave={clickable ? e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f8fafc') : undefined}
                    >
                      {row.map((cell, j) => (
                        <td key={j} style={{ padding: '7px 12px', borderBottom: '1px solid #f1f5f9', color: '#1e293b' }}>
                          {j === 0 && clickable
                            ? <span style={{ color: '#4B3FFF', fontWeight: 500 }}>{cell}</span>
                            : cell}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {displayItems.length === 0 && (
                  <tr>
                    <td colSpan={check.columns.length} style={{ padding: '16px 12px', color: '#94a3b8', textAlign: 'center' }}>
                      No records
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Paper>
  );
}

// ── Main component ─────────────────────────────────────────────
export default function DebrisAudit() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const { tickets, loading: ticketsLoading } = useAllTickets();
  const { loadReconciliations } = useTampaDebrisStore();

  const [running, setRunning] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [results, setResults] = useState<AuditResults | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Ticket drawer
  const [drawerTicketId, setDrawerTicketId] = useState<string | null>(null);
  const [drawerCheckLabel, setDrawerCheckLabel] = useState('');
  const [drawerData, setDrawerData] = useState<Record<string, any> | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const FL2 = FIELDS.debrisLoadTickets;
  const FTr2 = FIELDS.debrisTrucks;
  const FS2 = FIELDS.debrisDisposalSites;
  const FC2 = FIELDS.debrisContractors;

  const openDrawer = useCallback(async (ticketId: string, checkLabel: string) => {
    setDrawerTicketId(ticketId);
    setDrawerCheckLabel(checkLabel);
    setDrawerData(null);
    setDrawerLoading(true);
    try {
      const row = await fetchRecord(TABLES.debrisLoadTickets, ticketId);
      const r = row as Record<string, any>;
      const truckId = String(r[FL2.truckId] ?? '');
      const siteId = String(r[FL2.disposalSiteId] ?? '');
      const contractorId = String(r[FL2.contractorId] ?? '');
      const [truckRes, siteRes, contractorRes] = await Promise.allSettled([
        truckId && truckId !== '0' ? fetchRecord(TABLES.debrisTrucks, truckId) : Promise.resolve(null),
        siteId && siteId !== '0' ? fetchRecord(TABLES.debrisDisposalSites, siteId) : Promise.resolve(null),
        contractorId ? fetchRecord(TABLES.debrisContractors, contractorId) : Promise.resolve(null),
      ]);
      setDrawerData({
        ticket: r,
        truck: truckRes.status === 'fulfilled' ? truckRes.value as Record<string, any> : null,
        site: siteRes.status === 'fulfilled' ? siteRes.value as Record<string, any> : null,
        contractor: contractorRes.status === 'fulfilled' ? contractorRes.value as Record<string, any> : null,
      });
    } catch { setDrawerData(null); }
    setDrawerLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const runAudit = useCallback(async () => {
    if (!activeIncident) return;
    setRunning(true);
    setResults(null);
    const incidentId = activeIncident.id?.toString() ?? '';

    try {
      // ── 1. Truck registry (record_id → truckNumber)
      // debrisTrucks has no incidentId — scope by contractors belonging to this incident
      setStatusMsg('Loading truck registry...');
      const truckNumberById = new Map<string, string>();
      {
        const FC_A = FIELDS.debrisContractors;
        const cRes = await queryRecords<Record<string, unknown>>(TABLES.debrisContractors, {
          filters: [{ field: FC_A.incidentId, operator: 'eq' as const, value: activeIncident.id }], limit: 200,
        });
        const incContractorIds = new Set(cRes.records.map(r => String(r.record_id)));
        const res = await queryRecords<Record<string, unknown>>(TABLES.debrisTrucks, { limit: 500 });
        (res.records as any[]).forEach(r => {
          // Only include trucks belonging to incident's contractors
          const cid = String(r[FTruck.contractorId] ?? '');
          if (incContractorIds.size > 0 && !incContractorIds.has(cid)) return;
          const num = String(r[FTruck.truckNumber] || '');
          if (num) truckNumberById.set(String(r.record_id), num);
        });
      }


      // ── 3. Document indexes (ticketPageIndex + truckCertIndex)
      setStatusMsg('Loading document indexes...');
      let tpi: Record<string, { file: string; page: number }> | null = null;
      let tci: Record<string, unknown> | null = null;
      const docsRes = await queryRecords<Record<string, unknown>>(TABLES.debrisDocuments, {
        filters: [{ field: FD.incidentId, operator: 'eq' as const, value: activeIncident.id }], limit: 500,
      });
      const allDocs = docsRes.records as any[];
      {

        const tpiRecs = allDocs.filter(r => String(r[FD.documentType] || '') === 'ticket_page_index');
        if (tpiRecs.length > 0) {
          const chunks = tpiRecs.filter(r => String(r[FD.notes] || '').startsWith('|||CHUNK|||'));
          if (chunks.length > 0) {
            const sorted = chunks.sort((a, b) => {
              const am = String(a[FD.notes]).match(/\|\|\|CHUNK\|\|\|(\d+)\//);
              const bm = String(b[FD.notes]).match(/\|\|\|CHUNK\|\|\|(\d+)\//);
              return parseInt(am?.[1] || '0') - parseInt(bm?.[1] || '0');
            });
            const json = sorted.map(r =>
              String(r[FD.notes]).replace(/^\|\|\|CHUNK\|\|\|\d+\/\d+\|\|\|/, '')
            ).join('');
            try { tpi = JSON.parse(json); } catch { /* */ }
          } else {
            try { tpi = JSON.parse(String(tpiRecs[tpiRecs.length - 1][FD.notes] || '')); } catch { /* */ }
          }
        }

        const tciRecs = allDocs.filter(r => String(r[FD.documentType] || '') === 'truck_cert_index');
        if (tciRecs.length > 0) {
          try { tci = JSON.parse(String(tciRecs[tciRecs.length - 1][FD.notes] || '')); } catch { /* */ }
        }
      }

      // ── 4. Reconciliations
      setStatusMsg('Loading reconciliations...');
      await loadReconciliations();
      const reconList = useTampaDebrisStore.getState().reconciliations;

      // ── 5. Run checks
      setStatusMsg('Analyzing ' + tickets.length.toLocaleString() + ' tickets...');
      const MAX_DISPLAY = 500;
      const checks: CheckResult[] = [];

      // Check 1: Missing load ticket PDF — check documents directly
      // Build set of ticket numbers that have a source document
      const coveredTicketNums = new Set<string>();
      // Source 1: ticket page index (if built)
      if (tpi) {
        Object.keys(tpi).forEach(tn => coveredTicketNums.add(tn.toLowerCase()));
      }
      // Source 2: search debrisDocuments for load_tickets type docs
      const loadTicketDocs = allDocs.filter(r =>
        String(r[FD.documentType] || '') === 'load_tickets' || String(r[FD.documentType] || '') === 'ticket_image'
      );
      loadTicketDocs.forEach(doc => {
        const notes = String(doc[FD.notes] || '').toLowerCase();
        const title = String(doc[FD.title] || '').toLowerCase();
        tickets.forEach(t => {
          if (t.ticketNumber && (notes.includes(t.ticketNumber.toLowerCase()) || title.includes(t.ticketNumber.toLowerCase()))) {
            coveredTicketNums.add(t.ticketNumber.toLowerCase());
          }
        });
      });
      const noScan = tickets.filter(t => !t.ticketNumber || !coveredTicketNums.has(t.ticketNumber.toLowerCase()));
      checks.push({
        id: 'no-scan',
        label: 'Missing Load Ticket PDF',
        description: 'Tickets without a corresponding source PDF in the documentation archive.',
        severity: 'critical',
        passed: noScan.length === 0,
        count: noScan.length,
        columns: ['Ticket Number', 'Truck', 'Load Date'],
        items: noScan.slice(0, MAX_DISPLAY).map(t => [
          t.ticketNumber || String(t.id),
          t.truckNumber || truckNumberById.get(t.truckId) || t.truckId || '—',
          t.loadDate || '—',
        ]),
        itemIds: noScan.slice(0, MAX_DISPLAY).map(t => String(t.id)),
        truncated: noScan.length > MAX_DISPLAY,
      });

      // Check 2: Missing truck cert — check truck registry directly
      const trucksOnTickets = new Map<string, number>();
      tickets.forEach(t => {
        const num = t.truckNumber || truckNumberById.get(t.truckId) || '';
        if (!num) return;
        trucksOnTickets.set(num, (trucksOnTickets.get(num) || 0) + 1);
      });
      const noCert: Array<[string, number]> = [];
      // Load trucks directly — store may not have them if user navigated here first
      let truckRegistry = useDebrisStore.getState().trucks;
      if (truckRegistry.length === 0) {
        await useDebrisStore.getState().fetchTrucks();
        truckRegistry = useDebrisStore.getState().trucks;
      }
      trucksOnTickets.forEach((count, num) => {
        // Check truck registry for certification status
        const truck = truckRegistry.find(tr => tr.truckNumber === num);
        const isCertified = truck && (
          (truck as any).certStatus === 'certified' ||
          (truck as any).certStatus === 'active'
        );
        // Also check truck cert index as fallback
        const inIndex = tci && tci[num];
        if (!isCertified && !inIndex) noCert.push([num, count]);
      });
      checks.push({
        id: 'no-cert',
        label: 'Missing Truck Certification',
        description: 'Trucks used on load tickets without a valid certification in the truck registry.',
        severity: 'critical',
        passed: noCert.length === 0,
        count: noCert.length,
        columns: ['Truck Number', 'Ticket Count'],
        items: noCert.slice(0, MAX_DISPLAY).map(([num, cnt]) => [num, String(cnt)]),
        truncated: noCert.length > MAX_DISPLAY,
      });

      // Check 3: Zero CYD
      const zeroCYD = tickets.filter(t => (parseFloat(t.calculatedCubicYards) || 0) <= 0);
      checks.push({
        id: 'zero-cyd',
        label: 'Zero Cubic Yards Measured',
        description: 'Tickets with 0 or missing cubic yard measurement. These will not generate FEMA reimbursement.',
        severity: 'critical',
        passed: zeroCYD.length === 0,
        count: zeroCYD.length,
        columns: ['Ticket Number', 'CYD', 'Load Date'],
        items: zeroCYD.slice(0, MAX_DISPLAY).map(t => [
          t.ticketNumber || String(t.id),
          t.calculatedCubicYards || '0',
          t.loadDate || '—',
        ]),
        itemIds: zeroCYD.slice(0, MAX_DISPLAY).map(t => String(t.id)),
        truncated: zeroCYD.length > MAX_DISPLAY,
      });

      // Check 4: Missing disposal site — check both ID and name (TDMS imports have name only)
      const noDisposal = tickets.filter(t => {
        const hasId = t.disposalSiteId && t.disposalSiteId !== '0';
        const hasName = (t as any).disposalSiteName && (t as any).disposalSiteName.trim();
        return !hasId && !hasName;
      });
      checks.push({
        id: 'no-disposal',
        label: 'Missing Disposal Site',
        description: 'Tickets with no disposal site linked. FEMA requires a documented disposal destination for all loads.',
        severity: 'important',
        passed: noDisposal.length === 0,
        count: noDisposal.length,
        columns: ['Ticket Number', 'Truck', 'Load Date'],
        items: noDisposal.slice(0, MAX_DISPLAY).map(t => [
          t.ticketNumber || String(t.id),
          t.truckNumber || truckNumberById.get(t.truckId) || '—',
          t.loadDate || '—',
        ]),
        itemIds: noDisposal.slice(0, MAX_DISPLAY).map(t => String(t.id)),
        truncated: noDisposal.length > MAX_DISPLAY,
      });

      // Check 5 (removed: zone assignment is optional, not required for FEMA)

      // Check 6: Missing truck (no FK link AND no TMETA truck number)
      // TDMS-imported tickets store truck number in TMETA but may not have a formal truckId FK.
      // A ticket is only "missing a truck" if it has neither.
      const noTruck = tickets.filter(t => {
        const hasLink = t.truckId && t.truckId !== '0' && t.truckId !== '';
        const hasNumber = t.truckNumber && t.truckNumber !== '';
        return !hasLink && !hasNumber;
      });
      checks.push({
        id: 'no-truck',
        label: 'Ticket With No Truck Identified',
        description: 'Tickets with no truck number or truck record linked. Every load must be tied to an identified vehicle.',
        severity: 'important',
        passed: noTruck.length === 0,
        count: noTruck.length,
        columns: ['Ticket Number', 'Load Date'],
        items: noTruck.slice(0, MAX_DISPLAY).map(t => [
          t.ticketNumber || String(t.id),
          t.loadDate || '—',
        ]),
        itemIds: noTruck.slice(0, MAX_DISPLAY).map(t => String(t.id)),
        truncated: noTruck.length > MAX_DISPLAY,
      });

      // Check 7: Unresolved reconciliation exceptions
      const unresolvedRecon = reconList.filter(r => {
        if (r.status === 'approved' || r.status === 'resolved') return false;
        return Array.isArray(r.exceptions) && r.exceptions.length > 0;
      });
      checks.push({
        id: 'recon-exceptions',
        label: 'Unresolved Reconciliation Exceptions',
        description: 'Reconciliation records with open exceptions. All exceptions must be resolved before FEMA submission.',
        severity: 'important',
        passed: unresolvedRecon.length === 0,
        count: unresolvedRecon.length,
        columns: ['Contractor', 'Period', 'Status', 'Exceptions'],
        items: unresolvedRecon.map(r => [
          r.contractorId || '—',
          r.periodStart ? `${r.periodStart} – ${r.periodEnd}` : '—',
          r.status,
          String(r.exceptions.length),
        ]),
      });

      // Auto-expand all failing checks
      setExpanded(new Set(checks.filter(c => !c.passed).map(c => c.id)));
      setResults({ ranAt: new Date(), checks, totalTickets: tickets.length });
      setStatusMsg('');
    } catch (err) {
      console.error('[DebrisAudit] Failed:', err);
      setStatusMsg('Audit failed — see browser console for details.');
    } finally {
      setRunning(false);
    }
  }, [activeIncident, tickets, loadReconciliations]);

  // ── Summary stats ──────────────────────────────────────────────
  const criticalFails = results ? results.checks.filter(c => c.severity === 'critical' && !c.passed).length : 0;
  const importantFails = results ? results.checks.filter(c => c.severity === 'important' && !c.passed).length : 0;
  const passed = results ? results.checks.filter(c => c.passed).length : 0;
  const total = results ? results.checks.length : 0;

  const isReady = !ticketsLoading && !!activeIncident;

  return (
    <Stack gap={20} style={{ marginTop: 24 }}>
      {/* Header */}
      <Paper style={{ padding: '20px 24px' }}>
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3} style={{ color: '#1e293b', marginBottom: 4 }}>Pre-Submission Audit</Title>
            <Text size="sm" c="dimmed">
              Global gap analysis for{' '}
              <strong>{activeIncident?.name ?? 'active disaster'}</strong>.{' '}
              Checks source documentation, certifications, measurements, and reconciliations before FEMA submission.
            </Text>
            {results && (
              <Text size="xs" c="dimmed" style={{ marginTop: 6 }}>
                Last run: {results.ranAt.toLocaleString()} &mdash; {results.totalTickets.toLocaleString()} tickets analyzed
              </Text>
            )}
          </div>
          <Group gap={10} style={{ flexShrink: 0 }}>
            {ticketsLoading && (
              <Text size="xs" c="dimmed">Loading tickets...</Text>
            )}
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={runAudit}
              loading={running}
              disabled={!isReady || running}
            >
              {running ? statusMsg || 'Running...' : 'Run Audit'}
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* Summary cards — shown after first run */}
      {results && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {/* Critical */}
          <Paper style={{
            padding: '20px 24px',
            borderRadius: 12,
            border: '1px solid #e8eaf0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            transition: 'all 0.15s',
          }}>
            <Group gap={12} align="center">
              <IconAlertTriangle size={28} color="#D33423" />
              <div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#D33423', lineHeight: 1 }}>
                  {criticalFails}
                </div>
                <Text size="sm" style={{ color: '#1e293b', fontWeight: 600, marginTop: 4 }}>Critical Issues</Text>
                <Text size="xs" c="dimmed">Block submission</Text>
              </div>
            </Group>
          </Paper>

          {/* Important */}
          <Paper style={{
            padding: '20px 24px',
            borderRadius: 12,
            border: '1px solid #e8eaf0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            transition: 'all 0.15s',
          }}>
            <Group gap={12} align="center">
              <IconAlertCircle size={28} color="#885604" />
              <div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#885604', lineHeight: 1 }}>
                  {importantFails}
                </div>
                <Text size="sm" style={{ color: '#1e293b', fontWeight: 600, marginTop: 4 }}>Important Issues</Text>
                <Text size="xs" c="dimmed">Should be resolved</Text>
              </div>
            </Group>
          </Paper>

          {/* Passed */}
          <Paper style={{
            padding: '20px 24px',
            borderRadius: 12,
            border: '1px solid #e8eaf0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            transition: 'all 0.15s',
          }}>
            <Group gap={12} align="center">
              <IconShieldCheck size={28} color="#037730" />
              <div>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#037730', lineHeight: 1 }}>
                  {passed}/{total}
                </div>
                <Text size="sm" style={{ color: '#1e293b', fontWeight: 600, marginTop: 4 }}>Checks Passed</Text>
                <Text size="xs" c="dimmed">{passed === total ? 'Ready to submit' : 'Issues remain'}</Text>
              </div>
            </Group>
          </Paper>
        </div>
      )}

      {/* Overall pass banner */}
      {results && criticalFails === 0 && importantFails === 0 && (
        <Alert color="green" icon={<IconCheck size={18} />}>
          All checks passed. This disaster record appears ready for FEMA submission review.
        </Alert>
      )}

      {/* Check cards */}
      {results && (
        <Stack gap={8}>
          <Group justify="space-between" align="center" style={{ marginBottom: 4 }}>
            <Text size="sm" fw={600} c="dimmed">CRITICAL CHECKS</Text>
          </Group>
          {results.checks.filter(c => c.severity === 'critical').map(check => (
            <CheckCard
              key={check.id}
              check={check}
              expanded={expanded.has(check.id)}
              onToggle={() => toggle(check.id)}
              onRowClick={id => openDrawer(id, check.label)}
            />
          ))}

          <Group justify="space-between" align="center" style={{ marginTop: 8, marginBottom: 4 }}>
            <Text size="sm" fw={600} c="dimmed">IMPORTANT CHECKS</Text>
          </Group>
          {results.checks.filter(c => c.severity === 'important').map(check => (
            <CheckCard
              key={check.id}
              check={check}
              expanded={expanded.has(check.id)}
              onToggle={() => toggle(check.id)}
              onRowClick={id => openDrawer(id, check.label)}
            />
          ))}
        </Stack>
      )}

      {/* Empty state */}
      {!results && !running && (
        <Paper style={{ padding: '48px 24px', textAlign: 'center' }}>
          <IconShieldCheck size={48} color="#94a3b8" style={{ marginBottom: 12 }} />
          <Text size="lg" fw={600} c="dimmed" style={{ marginBottom: 8 }}>No audit results yet</Text>
          <Text size="sm" c="dimmed" style={{ maxWidth: 420, margin: '0 auto' }}>
            Click <strong>Run Audit</strong> to scan all tickets, truck certifications, and reconciliations for gaps.
            {ticketsLoading ? ' Waiting for tickets to load...' : ''}
          </Text>
        </Paper>
      )}

      {/* ── Ticket detail drawer ─────────────────────────── */}
      <Modal
        opened={!!drawerTicketId}
        onClose={() => { setDrawerTicketId(null); setDrawerData(null); }}
        title={drawerData
          ? `Ticket ${String(drawerData.ticket?.[FL2.ticketNumber] || drawerTicketId)}`
          : 'Loading ticket...'}
        size="lg"
        centered
      >
        {drawerLoading && (
          <Text size="sm" c="dimmed" style={{ padding: '24px 0', textAlign: 'center' }}>Loading...</Text>
        )}

        {!drawerLoading && drawerData && (() => {
          const t = drawerData.ticket;
          const truck = drawerData.truck;
          const site = drawerData.site;
          const contractor = drawerData.contractor;
          const FTr = FTr2; const FS = FS2; const FC = FC2;

          const debrisType = String(t[FL2.debrisType] ?? '');
          const status = String(t[FL2.status] ?? '') as TicketStatus;
          const cyd = parseFloat(String(t[FL2.calculatedCubicYards] ?? '0')) || 0;
          const loadDate = String(t[FL2.loadDate] ?? '') || String(t[FL2.pickupTime] ?? '');

          return (
            <Stack gap={12}>
              {/* Audit context banner */}
              <Paper p="xs" radius="md" style={{ background: '#fef3c7', borderLeft: '3px solid #C68700' }}>
                <Text size="xs" fw={600} c="#885604">Flagged by audit: {drawerCheckLabel}</Text>
              </Paper>

              {/* Key fields */}
              <SimpleGrid cols={2} spacing="xs">
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge size="sm" style={{ background: '#e2e8f0', color: '#1e293b' }}>
                    {TICKET_STATUS_LABELS[status] ?? status}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Debris Type</Text>
                  <Text size="sm" fw={500}>{(DEBRIS_TYPE_LABELS as any)[debrisType] ?? (debrisType || '—')}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Cubic Yards</Text>
                  <Text size="sm" fw={600} c={cyd === 0 ? 'var(--color-error)' : undefined}>{cyd === 0 ? '0 — missing' : cyd.toFixed(2)}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Load Date</Text>
                  <Text size="sm">{loadDate ? loadDate.slice(0, 10) : '—'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Loading Monitor</Text>
                  <Text size="sm">{String(t[FL2.loadingMonitorName] ?? '') || '—'}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Tower Monitor</Text>
                  <Text size="sm">{String(t[FL2.towerMonitorName] ?? '') || '—'}</Text>
                </div>
              </SimpleGrid>

              <Divider />

              {/* Truck */}
              <div>
                <Text size="xs" c="dimmed" fw={600} mb={4}>Truck</Text>
                {truck
                  ? <SimpleGrid cols={2} spacing="xs">
                      <div><Text size="xs" c="dimmed">Number</Text><Text size="sm" fw={500}>{String(truck[FTr.truckNumber] ?? '—')}</Text></div>
                      <div><Text size="xs" c="dimmed">Plate</Text><Text size="sm">{String(truck[FTr.licensePlate] ?? '—')}</Text></div>
                      <div><Text size="xs" c="dimmed">Driver</Text><Text size="sm">{String(truck[FTr.driverName] ?? '—')}</Text></div>
                      <div><Text size="xs" c="dimmed">Cert Status</Text><Text size="sm">{String(truck[FTr.status] ?? '—')}</Text></div>
                    </SimpleGrid>
                  : <Text size="sm" c="dimmed">No truck linked</Text>
                }
              </div>

              <Divider />

              {/* Disposal site */}
              <div>
                <Text size="xs" c="dimmed" fw={600} mb={4}>Disposal Site</Text>
                {site
                  ? <SimpleGrid cols={2} spacing="xs">
                      <div><Text size="xs" c="dimmed">Name</Text><Text size="sm" fw={500}>{String(site[FS.siteName] ?? '—')}</Text></div>
                      <div><Text size="xs" c="dimmed">Type</Text><Text size="sm">{String(site[FS.siteType] ?? '—')}</Text></div>
                    </SimpleGrid>
                  : <Text size="sm" c="var(--color-error)">No disposal site linked</Text>
                }
              </div>

              <Divider />

              {/* Contractor */}
              <div>
                <Text size="xs" c="dimmed" fw={600} mb={4}>Contractor</Text>
                {contractor
                  ? <Text size="sm" fw={500}>{String(contractor[FC.companyName] ?? '—')}</Text>
                  : <Text size="sm" c="dimmed">No contractor linked</Text>
                }
              </div>

              {/* Footer */}
              <Group justify="flex-end" mt={4}>
                <Button
                  size="xs"
                  variant="light"
                  rightSection={<IconExternalLink size={13} />}
                  onClick={() => navigate(`/debris/ticket/${drawerTicketId}`)}
                >
                  Open full Ticket360
                </Button>
              </Group>
            </Stack>
          );
        })()}

        {!drawerLoading && !drawerData && (
          <Text size="sm" c="dimmed" style={{ textAlign: 'center', padding: '24px 0' }}>
            Could not load ticket data.
          </Text>
        )}
      </Modal>
    </Stack>
  );
}
